import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getResponses, invalidateCache, CACHE_POLICY } from "./Utility/responseCache";
import SpreadsheetGrid from "./Spreadsheetgrid";
import MessageDialog from "../Utility/LinkBox";
import { UploadProgressOverlay } from "../Utility/UploadProgressOverlay";
import { checkTestResponses } from "../../Backend/backendService";

export default function ResponseViewer({ cachePolicy = CACHE_POLICY.USE_CACHE }) {
  const { state } = useLocation();
  const navigate = useNavigate();
  const testDetail = state?.test;

  const [data, setData] = useState({});
  const [activeQ, setActiveQ] = useState(null);
  const [wrap, setWrap] = useState(false);
  const [showMessage, setMessage] = useState(null);
  const [uploadSteps, setUploadSteps] = useState(null);

  useEffect(() => {
    if (!testDetail) navigate(-1);
  }, [testDetail, navigate]);

  const loadAll = useCallback(async () => {
    if (!testDetail?.questions?.length) return;
    await Promise.all(
      testDetail.questions.map(async (questionId) => {
        try {
          const result = await getResponses(testDetail.id, questionId, cachePolicy);
          setData(prev => ({ ...prev, [questionId]: result }));
        } catch (err) {
          console.error(`failed for ${questionId}:`, err);
        }
      })
    );
  }, [testDetail?.id, cachePolicy]);

  useEffect(() => {
    if (!testDetail?.questions?.length) return;
    setActiveQ(testDetail.questions[0]);
    loadAll();
  }, [testDetail?.id]);

  const handleCheckAgain = async() => {
    setUploadSteps([
      { label: "Sending Test Detail to Backend", status: "loading" }
    ]);
    const testId = testDetail.id;
    if (!testId.trim()) {
      setUploadSteps(null);
      setMessage({ vibe: "failure", title: "Invalid Test Id!", message: "" });
      return;
    }

    try {
      const result = await checkTestResponses(testId);
      setUploadSteps(null);
      setMessage({ vibe: "success", title: "Grading Started!", message: `✓ ${result.message}` });
      invalidateCache(testDetail.id);
      loadAll();
    } catch (err) {
      setUploadSteps(null);
      setMessage({ vibe: "failure", title: "Grading Failed!", message: `✗ ${err.message}` });
    }

  };

  if (!testDetail) return null;

  const active = data[activeQ];

  return (
    <>
      <div style={{ padding: 16, fontFamily: "sans-serif" }}>

        {/* ── test meta ── */}
        <div style={{ display: "flex", gap: 48, marginBottom: 20, borderBottom: "1px solid #eee", paddingBottom: 16 }}>
          {[
            ["Title", testDetail.title],
            ["Subject", testDetail.subject],
            ["Questions", testDetail.no_question],
          ].map(([label, val]) => (
            <div key={label}>
              <div style={{ fontSize: 12, color: "#888" }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 600 }}>{val}</div>
            </div>
          ))}
        </div>

        {/* ── tab row ── */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 8 }}>
          <div style={{ display: "flex", gap: 6, flex: 1, flexWrap: "wrap" }}>
            {testDetail.questions.map((qid, i) => (
              <button
                key={qid}
                onClick={() => setActiveQ(qid)}
                style={{
                  padding: "5px 14px", border: "1px solid #ccc", borderRadius: 6,
                  background: activeQ === qid ? "#111" : "transparent",
                  color: activeQ === qid ? "#fff" : "#333",
                  cursor: "pointer", fontSize: 13,
                }}
              >
                Q{i + 1}
              </button>
            ))}
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={wrap}
              onChange={e => setWrap(e.target.checked)}
            />
            Wrap
          </label>

          <button onClick={() => { }} style={{ padding: "5px 14px", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
            Export
          </button>
          <button onClick={handleCheckAgain} style={{ padding: "5px 14px", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
            Check again
          </button>
        </div>

        {/* ── grid ── */}
        {!active && <div style={{ color: "#999", fontSize: 13 }}>loading...</div>}
        {active && (
          <SpreadsheetGrid
            rows={active.matrix}
            columns={active.columns}
            cellColors={{}}
            wrap={wrap}
          />
        )}

      </div>
      {showMessage && (
        <MessageDialog
          vibe={showMessage.vibe}
          title={showMessage.title}
          message={showMessage.message}
          onClose={() => setMessage(null)}
        />
      )}
      {uploadSteps && <UploadProgressOverlay steps={uploadSteps} />}
    </>
  );
}